import React, { FC } from 'react';
import _ from 'lodash';
import { Box, Divider, Text, Checkbox } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Category, Service } from '@wix/ambassador-category-services/types';
import { useExploreContext } from '@components/Contexts/ExploreContext';
import MobileFilterModalLayout from '@components/MobileFilter/MobileFilterModalLayout';
import s from './MobileServices.module.scss';

type ServiceWithSelection = Service & {
  isSelected: boolean;
};

type CategoryWithSelection = Category & {
  isSelected: boolean;
  servicePaths: Record<string, ServiceWithSelection>;
};

type Props = {
  isCategorySelected: (category: string) => boolean | undefined;
  hasSelectionData: boolean;
  selection: Record<string, CategoryWithSelection>;
  selectAService: (service: Service, category: string) => void;
  isServiceSelected: (
    service: Service,
    category: string,
  ) => boolean | undefined;
  selectCategory: (category: string) => void;
  onClear: () => void;
  onApply: () => void;
  onBack: (() => void) | undefined;
  onClose: (() => void) | undefined;
  closeModal: (() => void) | undefined;
  numSelectedServices: number;
};

const Services: FC<Props> = ({
  isCategorySelected,
  hasSelectionData,
  selection,
  selectAService,
  isServiceSelected,
  selectCategory,
  onClear,
  onApply,
  onBack,
  onClose,
  closeModal,
  numSelectedServices,
}) => {
  const { t } = useTranslation();
  const {
    state: { services },
  } = useExploreContext();

  const DividerWithSpace = () => {
    return (
      <Box paddingTop="24px" paddingBottom="24px">
        <Divider />
      </Box>
    );
  };

  const renderService = (service: Service, category: string) => {
    const { id, displayKey } = service;

    return (
      <div
        key={id}
        onClick={() => {
          selectAService(service, category);
        }}
        className={s.tappableRow}
      >
        <Box paddingTop="12px" paddingBottom="12px" paddingLeft="30px">
          <Checkbox
            dataHook={`${id}-checkbox`}
            id={id}
            checked={isServiceSelected(service, category)}
            onChange={() => {
              selectAService(service, category);
            }}
          >
            <Text weight="thin">{t(`explore.services.${t(displayKey!)}`)}</Text>
          </Checkbox>
        </Box>
      </div>
    );
  };

  const renderCategorySection = (
    category: string,
    servicesForCategory: Array<Service>,
  ) => {
    const isIndeterminate =
      !isCategorySelected(category) &&
      hasSelectionData &&
      _.size(_.filter(selection[category].servicePaths, (s) => s.isSelected!)) >
        0;

    return (
      <div key={category}>
        <Box direction="vertical">
          <Checkbox
            onChange={() => {
              selectCategory(category);
            }}
            checked={isCategorySelected(category)}
            indeterminate={isIndeterminate}
            dataHook={`${category}-subheader-checkbox`}
          >
            <Text weight="thin" dataHook={`${category}-subheader`}>
              {t(`explore.services.${category}`)}
            </Text>
          </Checkbox>
          <Box height="12px" />
          {_.map(servicesForCategory, (service) => {
            return renderService(service, category);
          })}
        </Box>
        {category !== 'graphicdesign' && DividerWithSpace()}
      </div>
    );
  };

  return (
    <MobileFilterModalLayout
      title={t('mobile-services-title')}
      selectedAmount={numSelectedServices || 0}
      content={
        <div
          data-hook="mobile-services-filter"
          style={{
            paddingLeft: '24px',
            paddingRight: '24px',
            paddingTop: '12px',
          }}
        >
          {_.map(services, (category) => {
            const categoriesServices = category.services;
            return renderCategorySection(
              category.displayKey!,
              categoriesServices!,
            );
          })}
          <Box height="12px" />
        </div>
      }
      onClear={() => {
        onClear();
      }}
      onApply={() => {
        onApply();
        closeModal && closeModal();
      }}
      onClose={() => {
        onClose && onClose();
      }}
      onBack={() => {
        onBack && onBack();
      }}
    />
  );
};

export default Services;
